import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, FlaskConical, BookMarked, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import PageBlocks from "@/components/PageBlocks";

const LABS = [
  {
    code: "LDMS",
    to: "/directions/bibliotheques-musees/cecom/laboratoires/ldms",
    title: "Discours, Médias et Société",
    desc: "Analyse les relations entre médias, discours publics et transformations sociopolitiques : genre, culture et participation citoyenne.",
    color: "#0A2E52",
  },
  {
    code: "LCOT",
    to: "/directions/bibliotheques-musees/cecom/laboratoires/lcot",
    title: "Communication, Organisations et Transitions",
    desc: "Étudie les pratiques de communication stratégique, la gouvernance, le développement durable et la communication pour le changement social.",
    color: "#D4AF37",
  },
  {
    code: "LEIHN",
    to: "/directions/bibliotheques-musees/cecom/laboratoires/leihn",
    title: "Écosystèmes Informationnels et Humanités Numériques",
    desc: "Se consacre aux systèmes d'information, à la documentation, aux technologies numériques et à l'innovation communicationnelle.",
    color: "#144C86",
  },
];

const DEFAULTS = {
  eyebrow: "Centre de recherche",
  title: "Centre d'Études en Communication (CECOM)",
  banner_image: "/media/research-lab.jpg",
  intro:
    "Le CECOM est le centre de recherche du Secrétariat Général à la Recherche de l'UNISIC. Il conduit des travaux appliqués sur les mutations médiatiques, la communication publique et les usages du numérique en Afrique centrale.",
  missions:
    "• Animer les axes de recherche en communication\n• Publier la revue Les Cahiers Congolais de Communication\n• Encadrer des projets étudiants appliqués\n• Coopérer avec les partenaires académiques et institutionnels",
  axes: "",
  activites:
    "Séminaires mensuels, journées d'études annuelles, colloques scientifiques et publications régulières.",
  lab_title: "Laboratoires de Recherche",
  lab_content:
    "Le CECOM structure ses activités scientifiques autour de trois laboratoires thématiques qui explorent, chacun sous un angle spécifique, les mutations médiatiques, communicationnelles et informationnelles en République Démocratique du Congo et en Afrique centrale.",
  cahiers_title: "Les Cahiers Congolais de Communication",
  cahiers_content:
    "Revue scientifique éditée par le Centre d'Études en Communication depuis Avril 2002. Elle publie des articles de recherche originaux en sciences de l'information et de la communication. Ouverte aux enseignants-chercheurs, doctorants et chercheurs junior.",
  cahiers_external_url: "",
};

export default function Cecom() {
  const [c, setC] = useState(DEFAULTS);
  useEffect(() => {
    api.get("/content/service.cecom").then((r) => setC({ ...DEFAULTS, ...(r.data || {}) })).catch(() => {});
  }, []);

  return (
    <div data-testid="cecom-page">
      <section className="relative h-[46vh] min-h-[340px] overflow-hidden">
        <img src={c.banner_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-veil" />
        <div className="relative h-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-14">
          <div className="eyebrow" style={{ color: "#E5C158" }}>{c.eyebrow}</div>
          <h1 className="mt-3 text-white text-4xl sm:text-5xl font-bold tracking-tight">{c.title}</h1>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-unisic-gold" />
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-10">
            <div>
              <div className="eyebrow eyebrow-dark">Présentation</div>
              <span className="divider-gold mt-3" />
              <p className="mt-6 text-lg text-slate-700 leading-relaxed whitespace-pre-line">{c.intro}</p>
              <PageBlocks blocks={c.blocks} legacyBody={c.body} className="mt-6" />
            </div>
            {(c.axes || "").trim() && (
              <div>
                <h2 className="text-xl font-bold text-unisic-blue">Axes de recherche</h2>
                <p className="mt-3 text-sm text-slate-700 whitespace-pre-line">{c.axes}</p>
              </div>
            )}
            {(c.activites || "").trim() && (
              <div>
                <h2 className="text-xl font-bold text-unisic-blue">Activités scientifiques</h2>
                <p className="mt-3 text-sm text-slate-700 whitespace-pre-line">{c.activites}</p>
              </div>
            )}
          </div>
          <aside className="paper-card p-6 h-fit">
            <div className="eyebrow eyebrow-dark">Missions</div>
            <span className="divider-gold mt-2" />
            <p className="mt-4 text-sm text-slate-700 whitespace-pre-line">{c.missions}</p>
          </aside>
        </div>

        {/* BLOC 1 : Laboratoires de Recherche */}
        <div className="mt-16 paper-card p-8 md:p-10 relative overflow-hidden" data-testid="cecom-bloc-labo">
          <div className="absolute top-0 right-0 opacity-5"><FlaskConical className="w-64 h-64 text-unisic-blue" /></div>
          <div className="relative">
            <div className="eyebrow eyebrow-dark">Bloc 1</div>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-unisic-blue flex items-center gap-3">
              <FlaskConical className="w-7 h-7 text-unisic-gold" /> {c.lab_title}
            </h2>
            <span className="divider-gold mt-3" />
            <p className="mt-6 text-slate-700 leading-relaxed whitespace-pre-line max-w-3xl">{c.lab_content}</p>

            <div className="mt-10 grid gap-6 md:grid-cols-3" data-testid="cecom-labs-grid">
              {LABS.map((lab) => (
                <Link
                  key={lab.code}
                  to={lab.to}
                  className="group paper-card card-hover p-6 border-t-4 flex flex-col"
                  style={{ borderColor: lab.color }}
                  data-testid={`lab-card-${lab.code.toLowerCase()}`}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="inline-flex items-center justify-center w-11 h-11 rounded-sm"
                      style={{ background: `${lab.color}18`, color: lab.color }}
                    >
                      <FlaskConical className="w-5 h-5" />
                    </span>
                    <span className="text-[11px] font-mono tracking-widest text-slate-500">{lab.code}</span>
                  </div>
                  <h3 className="mt-4 font-bold text-unisic-blue text-lg leading-snug">
                    {lab.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{lab.desc}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-unisic-blue group-hover:text-unisic-gold transition-colors">
                    Découvrir <ArrowRight className="w-4 h-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* BLOC 2 : Cahiers Congolais de Communication */}
        <div className="mt-10 paper-card p-8 md:p-10 relative overflow-hidden" data-testid="cecom-bloc-cahiers">
          <div className="absolute top-0 right-0 opacity-5"><BookMarked className="w-64 h-64 text-unisic-gold" /></div>
          <div className="relative">
            <div className="eyebrow eyebrow-dark">Bloc 2</div>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold text-unisic-blue flex items-center gap-3">
              <BookMarked className="w-7 h-7 text-unisic-gold" /> {c.cahiers_title}
            </h2>
            <span className="divider-gold mt-3" />
            <p className="mt-6 text-slate-700 leading-relaxed whitespace-pre-line max-w-3xl">{c.cahiers_content}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/plateformes/revues" className="btn-primary text-sm" data-testid="btn-cahiers-interne">
                <BookMarked className="w-4 h-4" /> Consulter sur le portail
              </Link>
              {c.cahiers_external_url && (
                <a href={c.cahiers_external_url} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm" data-testid="btn-cahiers-externe">
                  <ExternalLink className="w-4 h-4" /> Site externe de la revue
                </a>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
